import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useAppDispatch } from './store/hooks';
import { setReducedMotion } from './store/slices/uiSlice';
import { prefersReducedMotion } from './utils/performance';
import { useScrollOptimization } from './hooks/useScrollOptimization';
import { useSEO } from './hooks/useSEO';
import './App.css';
import Header from './components/Header';
import Footer from './components/Footer';
import WhatsAppFloatingButton from './components/WhatsAppFloatingButton';
import ProtectedRoute from './components/ProtectedRoute';
import FirestoreErrorBoundary from './components/FirestoreErrorBoundary';
import { AuthProvider } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { AddWebsiteDialogProvider } from './context/AddWebsiteDialogContext';
import { PackageSelectionModalProvider } from './context/PackageSelectionModalContext';
import { SHOW_PRODUCTS_PAGE, SHOW_SERVICES_PAGE, SHOW_REFERRAL_SYSTEM } from './config/featureFlags';

// Lazy load components for better performance
const Home = lazy(() => import('./pages/Home'));
const Products = lazy(() => import('./pages/Products'));
const PricingPage = lazy(() => import('./pages/Pricing'));
const PackageDetails = lazy(() => import('./pages/PackageDetails'));
const WebsitePage = lazy(() => import('./pages/Website'));
const PackageDetailPage = lazy(() => import('./pages/PackageDetailPage'));
const ServicesPage = lazy(() => import('./pages/Services'));
const AiMlServicesPage = lazy(() => import('./pages/AiMlServices'));
const BusinessSolutionsPage = lazy(() => import('./pages/BusinessSolutions'));
const HotelManagementPage = lazy(() => import('./pages/HotelManagement'));
const RestaurantEventsPage = lazy(() => import('./pages/RestaurantEvents'));
const BusinessOperationsPage = lazy(() => import('./pages/BusinessOperations'));
const DigitalSolutionsPage = lazy(() => import('./pages/DigitalSolutions'));
const Contact = lazy(() => import('./pages/Contact'));
const FAQPage = lazy(() => import('./pages/FAQ'));
const ReferralPage = lazy(() => import('./pages/ReferralPage'));
const ServiceSingle = lazy(() => import('./pages/ServiceSingle'));
const Purchase = lazy(() => import('./pages/Purchase'));
const PaymentPage = lazy(() => import('./pages/PaymentPage'));
const SecurityDepositPayment = lazy(() => import('./pages/SecurityDepositPayment'));
const SecurityDepositSuccess = lazy(() => import('./pages/SecurityDepositSuccess'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Profile = lazy(() => import('./pages/Profile'));
const Checkout = lazy(() => import('./pages/Checkout'));
const OwnerDashboard = lazy(() => import('./pages/OwnerDashboard'));
const PartnerDashboard = lazy(() => import('./pages/PartnerDashboard'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const PolicyPage = lazy(() => import('./pages/PolicyPage'));
const Careers = lazy(() => import('./pages/Careers'));
const CareerDetail = lazy(() => import('./pages/CareerDetail'));
const Blogs = lazy(() => import('./pages/Blogs'));
const BlogDetail = lazy(() => import('./pages/BlogDetail'));

const LoadingFallback = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '50vh',
    fontSize: '1.2rem',
    color: '#FF6B35'
  }}>
    Loading...
  </div>
);

/** Scroll to top on route change. Skip for package detail sections (/website/:slug/:section) so sidebar nav clicks do not scroll to top. */
const PACKAGE_DETAIL_PATH_REGEX = /^\/website\/[^/]+\/[^/]+$/;
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (PACKAGE_DETAIL_PATH_REGEX.test(pathname)) return;
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppRoutes() {
  const location = useLocation();
  const dispatch = useAppDispatch();
  useSEO(location.pathname);
  const isOnboardingRoute = false;
  const isPaymentRoute = location.pathname === '/payment' || location.pathname.startsWith('/payment/') || location.pathname === '/security-deposit-payment';
  const isSecurityDepositPaymentRoute = location.pathname === '/security-deposit-payment';
  const isSuccessRoute = location.pathname === '/security-deposit-success';
  const isDashboardRoute = location.pathname.startsWith('/admin-dashboard') || location.pathname.startsWith('/owner') || location.pathname.startsWith('/partner');
  const isFullWidthLayoutRoute = isSecurityDepositPaymentRoute;

  // Optimize scroll performance
  useScrollOptimization();

  // Check for reduced motion preference
  useEffect(() => {
    dispatch(setReducedMotion(prefersReducedMotion()));

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = (e) => {
      dispatch(setReducedMotion(e.matches));
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [dispatch]);

  return (
    <div className={`layout ${isFullWidthLayoutRoute ? 'layout--get-started' : ''}`}>
      <ScrollToTop />
      {!isOnboardingRoute && !isPaymentRoute && !isSecurityDepositPaymentRoute && !isSuccessRoute && !isDashboardRoute && <Header />}
      <main className="layout__content">
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/products" element={SHOW_PRODUCTS_PAGE ? <Products /> : <Navigate to="/" replace />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/pricing/package/:packageName/:section?" element={<PackageDetails />} />
            <Route path="/website" element={<WebsitePage />} />
            <Route path="/website/:packageSlug/:section?" element={<PackageDetailPage />} />
            <Route path="/services" element={SHOW_SERVICES_PAGE ? <ServicesPage /> : <Navigate to="/" replace />} />
            <Route path="/services/aiml" element={SHOW_SERVICES_PAGE ? <AiMlServicesPage /> : <Navigate to="/" replace />} />
            <Route path="/services/business" element={SHOW_SERVICES_PAGE ? <BusinessSolutionsPage /> : <Navigate to="/" replace />} />
            <Route path="/services/hotel-management" element={SHOW_SERVICES_PAGE ? <HotelManagementPage /> : <Navigate to="/" replace />} />
            <Route path="/services/restaurant-events" element={SHOW_SERVICES_PAGE ? <RestaurantEventsPage /> : <Navigate to="/" replace />} />
            <Route path="/services/business-operations" element={SHOW_SERVICES_PAGE ? <BusinessOperationsPage /> : <Navigate to="/" replace />} />
            <Route path="/services/digital-solutions" element={SHOW_SERVICES_PAGE ? <DigitalSolutionsPage /> : <Navigate to="/" replace />} />
            {/* Legacy: keep only linked path */}
            <Route path="/pricing/website-packages" element={<WebsitePage />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/referral/:section?" element={SHOW_REFERRAL_SYSTEM ? <ReferralPage /> : <Navigate to="/" replace />} />
            <Route path="/service/:serviceName" element={<ServiceSingle />} />
            <Route path="/service" element={<ServiceSingle />} />
            <Route path="/purchase/:packageName" element={<Purchase />} />
            <Route path="/security-deposit-payment" element={<SecurityDepositPayment />} />
            <Route path="/security-deposit-success" element={<SecurityDepositSuccess />} />
            <Route path="/payment" element={<PaymentPage />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/owner" element={<ProtectedRoute requiredRole="WEBSITE_OWNER"><OwnerDashboard /></ProtectedRoute>} />
            <Route path="/partner" element={<ProtectedRoute requiredRole="SALES_PARTNER"><PartnerDashboard /></ProtectedRoute>} />
            <Route path="/admin-dashboard/*" element={<ProtectedRoute requiredRole="ADMIN"><AdminDashboard /></ProtectedRoute>} />
            <Route path="/legal/:policySlug" element={<PolicyPage />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/careers/:jobId" element={<CareerDetail />} />
            <Route path="/blogs" element={<Blogs />} />
            <Route path="/blogs/:blogId" element={<BlogDetail />} />
            {/* 404 Catch-all Route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      {!isOnboardingRoute && !isPaymentRoute && !isSuccessRoute && !isDashboardRoute && <Footer />}
      {!isOnboardingRoute && !isPaymentRoute && !isSecurityDepositPaymentRoute && !isSuccessRoute && !isDashboardRoute && <WhatsAppFloatingButton />}
    </div>
  );
}

function App() {
  return (
    <FirestoreErrorBoundary>
      <AuthProvider>
        <AddWebsiteDialogProvider>
          <Router>
            <LanguageProvider>
              <PackageSelectionModalProvider>
                <div className="App">
                  <AppRoutes />
                </div>
              </PackageSelectionModalProvider>
            </LanguageProvider>
          </Router>
        </AddWebsiteDialogProvider>
      </AuthProvider>
    </FirestoreErrorBoundary>
  );
}

export default App;
